<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="1000px"
    top="3vh"
    append-to-body
    :close-on-click-modal="false"
    @closed="resetForm"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-width="145px"
      v-loading="loading"
    >
      <div class="section-title">保单信息</div>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="投保人" prop="policyholder">
            <el-input
              v-model.trim="form.policyholder"
              :disabled="readonly"
              @blur="handlePolicyholderBlur"
              @input="syncTaxName"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="被保险人" prop="beneficiary">
            <el-input v-model.trim="form.beneficiary" :disabled="readonly" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="保单号" prop="policy_no">
            <el-input v-model.trim="form.policy_no" :disabled="readonly" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="渠道" prop="channel_id">
            <el-select
              v-model="form.channel_id"
              filterable
              clearable
              :disabled="readonly"
              placeholder="请选择渠道"
              style="width: 100%"
            >
              <el-option
                v-for="item in channelList"
                :key="item.id"
                :label="channelLabel(item)"
                :value="Number(item.id)"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="保险公司" prop="insurance_type">
            <el-select
              v-model="form.insurance_type"
              filterable
              clearable
              :disabled="readonly"
              placeholder="请选择保险公司"
              style="width: 100%"
            >
              <el-option
                v-for="item in insuranceCompanyList"
                :key="item.id"
                :label="item.name || item.insurance_company_name"
                :value="Number(item.id)"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="业务员手机号" prop="operator_mobile">
            <el-input
              v-model.trim="form.operator_mobile"
              :disabled="readonly"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="省份" prop="province">
            <el-select
              v-model="form.province"
              filterable
              clearable
              :disabled="readonly"
              style="width: 100%"
              @change="handleProvinceChange"
            >
              <el-option
                v-for="item in provinceList"
                :key="provinceName(item)"
                :label="provinceName(item)"
                :value="provinceName(item)"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="城市" prop="city">
            <el-select
              v-model="form.city"
              filterable
              clearable
              :disabled="readonly || !form.province"
              style="width: 100%"
            >
              <el-option
                v-for="item in cityList"
                :key="cityName(item)"
                :label="cityName(item)"
                :value="cityName(item)"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="起保时间" prop="effect_date">
            <el-date-picker
              v-model="form.effect_date"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              :disabled="readonly"
              style="width: 100%"
              @change="handleEffectDateChange"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="终保时间" prop="expire_date">
            <el-date-picker
              v-model="form.expire_date"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              :disabled="readonly"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <div class="section-title">保费与保额</div>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item
            label="预计营业收入(万元)"
            prop="estimated_operating_income"
          >
            <el-input-number
              v-model="form.estimated_operating_income"
              :min="0"
              :precision="2"
              :controls="false"
              :disabled="readonly"
              style="width: 100%"
              @change="calculateRate"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="保费(元)" prop="price">
            <el-input-number
              v-model="form.price"
              :min="0"
              :precision="2"
              :controls="false"
              :disabled="readonly"
              style="width: 100%"
              @change="calculateRate"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="单次赔偿限额(万元)" prop="single_coverage">
            <el-input-number
              v-model="form.single_coverage"
              :min="0"
              :precision="2"
              :controls="false"
              :disabled="readonly"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="累计赔偿限额(万元)" prop="total_coverage">
            <el-input-number
              v-model="form.total_coverage"
              :min="0"
              :precision="2"
              :controls="false"
              :disabled="readonly"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="费率(%)">
            <el-input v-model="form.rate" disabled />
          </el-form-item>
        </el-col>
      </el-row>

      <div class="section-title">开票信息</div>
      <el-form-item label="是否开票">
        <el-radio-group v-model="form.vat_invoice_open" :disabled="readonly">
          <el-radio :label="1">开票</el-radio>
          <el-radio :label="2">不开票</el-radio>
          <el-radio :label="4">待定</el-radio>
        </el-radio-group>
      </el-form-item>
      <template v-if="form.vat_invoice_open === 1">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="发票类型">
              <el-radio-group
                v-model="form.vat_invoice_type"
                :disabled="readonly"
              >
                <el-radio :label="1">普通发票</el-radio>
                <el-radio :label="2">专用发票</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col v-if="form.vat_invoice_type === 1" :span="12">
            <el-form-item label="抬头类型">
              <el-radio-group
                v-model="form.vat_invoice_title"
                :disabled="readonly"
              >
                <el-radio :label="1">企业</el-radio>
                <el-radio :label="2">个人</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col
            v-if="form.vat_invoice_type === 1 && form.vat_invoice_title === 2"
            :span="12"
          >
            <el-form-item label="个人抬头">
              <el-input
                v-model.trim="form.individual_title_name"
                :disabled="readonly"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row
          v-if="form.vat_invoice_type === 2 || form.vat_invoice_title === 1"
          :gutter="20"
        >
          <el-col :span="12"
            ><el-form-item label="企业名称"
              ><el-input
                v-model.trim="form.tax_name"
                :disabled="readonly"/></el-form-item
          ></el-col>
          <el-col :span="12"
            ><el-form-item label="纳税人识别号"
              ><el-input
                v-model.trim="form.tax_no"
                :disabled="readonly"/></el-form-item
          ></el-col>
          <el-col :span="12"
            ><el-form-item label="注册地址"
              ><el-input
                v-model.trim="form.tax_address"
                :disabled="readonly"/></el-form-item
          ></el-col>
          <el-col :span="12"
            ><el-form-item label="注册电话"
              ><el-input
                v-model.trim="form.tax_mobile"
                :disabled="readonly"/></el-form-item
          ></el-col>
          <el-col :span="12"
            ><el-form-item label="开户银行"
              ><el-input
                v-model.trim="form.tax_bank"
                :disabled="readonly"/></el-form-item
          ></el-col>
          <el-col :span="12"
            ><el-form-item label="银行账号"
              ><el-input
                v-model.trim="form.tax_bank_no"
                :disabled="readonly"/></el-form-item
          ></el-col>
        </el-row>
      </template>

      <div class="section-title">资料与约定</div>
      <el-form-item label="营业执照" prop="business_license_url">
        <file-upload v-model="form.business_license_url" :disabled="readonly" />
      </el-form-item>
      <el-form-item label="保单附件">
        <file-upload
          v-model="form.policy_image_urls"
          multiple
          :disabled="readonly"
        />
      </el-form-item>
      <el-form-item label="投保单附件">
        <file-upload
          v-model="form.form_image_urls"
          multiple
          :disabled="readonly"
        />
      </el-form-item>
      <el-form-item label="免赔约定">
        <el-input
          v-model="form.deductible"
          type="textarea"
          :rows="3"
          :disabled="readonly"
        />
      </el-form-item>
      <el-form-item label="特别约定">
        <el-input
          v-model="form.clause"
          type="textarea"
          :rows="3"
          :disabled="readonly"
        />
      </el-form-item>
      <el-form-item label="保险条款">
        <el-input
          v-model="form.terms"
          type="textarea"
          :rows="3"
          :disabled="readonly"
        />
      </el-form-item>
    </el-form>
    <span slot="footer">
      <el-button @click="dialogVisible = false">{{
        readonly ? "关闭" : "取消"
      }}</el-button>
      <el-button
        v-if="!readonly"
        type="primary"
        :loading="submitting"
        @click="submit"
      >
        保存
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import dayjs from "dayjs";
import {
  getCompanyInvoiceInfo,
  getOfflineBusinessChannelList,
  getOfflineBusinessCityList,
  getOfflineBusinessInsuranceCompanyList,
  getOfflineBusinessOrderInfo,
  getOfflineBusinessProvinceList,
  saveOfflineBusinessOrder
} from "@/api/offline-business-order";
import FileUpload from "./FileUpload.vue";

const createForm = () => ({
  id: "",
  channel_id: "",
  policyholder: "",
  beneficiary: "",
  policy_no: "",
  effect_date: "",
  expire_date: "",
  estimated_operating_income: undefined,
  single_coverage: undefined,
  total_coverage: undefined,
  price: undefined,
  rate: "",
  province: "",
  city: "",
  insurance_type: "",
  vat_invoice_open: 2,
  vat_invoice_type: 1,
  vat_invoice_title: 1,
  tax_name: "",
  tax_no: "",
  tax_address: "",
  tax_mobile: "",
  tax_bank: "",
  tax_bank_no: "",
  tax_file_url: "",
  vat_file_url: "",
  individual_title_name: "",
  business_license_url: "",
  operator_mobile: "",
  policy_image_urls: [],
  form_image_urls: [],
  deductible: "",
  clause: "",
  terms: ""
});

export default {
  name: "OfflineBusinessOrderFormDialog",
  components: { FileUpload },
  props: {
    visible: { type: Boolean, default: false },
    orderId: { type: [Number, String], default: "" },
    mode: { type: String, default: "add" }
  },
  data() {
    const validateMobile = (rule, value, callback) => {
      if (value && !/^1[3-9]\d{9}$/.test(value))
        callback(new Error("请输入正确的手机号"));
      else callback();
    };
    const validateExpireDate = (rule, value, callback) => {
      if (!value) callback();
      else if (
        this.form.effect_date &&
        dayjs(value).valueOf() < dayjs(this.form.effect_date).valueOf()
      ) {
        callback(new Error("终保时间不能早于起保时间"));
      } else callback();
    };
    return {
      loading: false,
      submitting: false,
      form: createForm(),
      channelList: [],
      provinceList: [],
      cityList: [],
      insuranceCompanyList: [],
      rules: {
        policyholder: [
          { required: true, message: "请输入投保人", trigger: "blur" }
        ],
        beneficiary: [
          { required: true, message: "请输入被保险人", trigger: "blur" }
        ],
        policy_no: [
          { required: true, message: "请输入保单号", trigger: "blur" }
        ],
        channel_id: [
          { required: true, message: "请选择渠道", trigger: "change" }
        ],
        insurance_type: [
          { required: true, message: "请选择保险公司", trigger: "change" }
        ],
        expire_date: [{ validator: validateExpireDate, trigger: "change" }],
        price: [{ required: true, message: "请输入保费", trigger: "change" }],
        operator_mobile: [{ validator: validateMobile, trigger: "blur" }]
      }
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      }
    },
    readonly() {
      return this.mode === "view";
    },
    dialogTitle() {
      return this.mode === "add"
        ? "新增年保订单"
        : this.mode === "edit"
        ? "编辑年保订单"
        : "查看年保订单";
    }
  },
  watch: {
    visible(value) {
      if (value) this.initialize();
    }
  },
  methods: {
    async initialize() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadChannels(),
          this.loadProvinces(),
          this.loadInsuranceCompanies()
        ]);
        if (this.mode !== "add" && this.orderId) await this.loadOrderInfo();
      } finally {
        this.loading = false;
      }
    },
    async loadChannels() {
      const res = await getOfflineBusinessChannelList();
      this.channelList = (res && (res.rows || res.data)) || [];
    },
    async loadProvinces() {
      const res = await getOfflineBusinessProvinceList();
      this.provinceList = (res && (res.rows || res.data)) || [];
    },
    async loadInsuranceCompanies() {
      const res = await getOfflineBusinessInsuranceCompanyList();
      this.insuranceCompanyList = (res && (res.rows || res.data)) || [];
    },
    async loadCities(province) {
      if (!province) {
        this.cityList = [];
        return;
      }
      const res = await getOfflineBusinessCityList({ province });
      this.cityList = (res && (res.rows || res.data)) || [];
    },
    async loadOrderInfo() {
      const res = await getOfflineBusinessOrderInfo({ id: this.orderId });
      const data = (res && (res.data || res.order || res.info)) || res || {};
      this.form = {
        ...createForm(),
        ...data,
        id: data.id || this.orderId,
        tax_name: data.tax_name || data.policyholder || "",
        channel_id:
          data.channel_id === "" || data.channel_id == null
            ? ""
            : Number(data.channel_id),
        insurance_type:
          data.insurance_type === "" || data.insurance_type == null
            ? ""
            : Number(data.insurance_type),
        vat_invoice_open: Number(data.vat_invoice_open || 2),
        vat_invoice_type: Number(data.vat_invoice_type || 1),
        vat_invoice_title: Number(data.vat_invoice_title || 1),
        effect_date: this.formatDate(data.effected_at || data.effect_date),
        expire_date: this.formatDate(data.expired_at || data.expire_date),
        price: this.toUnit(data.price, 100),
        rate: this.toUnit(data.rate, 10000),
        estimated_operating_income: this.toUnit(
          data.estimated_operating_income,
          10000
        ),
        single_coverage: this.toUnit(data.single_coverage, 10000),
        total_coverage: this.toUnit(data.total_coverage, 10000),
        policy_image_urls: this.parseUrls(data.policy_image_urls),
        form_image_urls: this.parseUrls(data.form_image_urls),
        business_license_url: this.parseUrls(data.business_license_url)[0] || ""
      };
      await this.loadCities(this.form.province);
    },
    async handlePolicyholderBlur() {
      if (!this.form.policyholder) return;
      if (!this.form.tax_name) this.form.tax_name = this.form.policyholder;
      if (this.mode !== "add") return;
      try {
        const res = await getCompanyInvoiceInfo({
          name: this.form.policyholder
        });
        const data = (res && (res.data || res.info)) || {};
        if (!Object.keys(data).length) return;
        this.form.business_license_url =
          data.license_file_url ||
          data.business_license_url ||
          this.form.business_license_url;
        this.form.vat_file_url = data.vat_file_url || "";
        this.form.tax_file_url = data.tax_file_url || "";
        this.form.tax_name =
          data.name || data.tax_name || this.form.policyholder;
        this.form.tax_no = data.license_no || data.tax_no || "";
        this.form.tax_address = data.tax_address || data.address || "";
        this.form.tax_mobile = data.tax_mobile || data.mobile || "";
        this.form.tax_bank = data.tax_bank || data.bank || "";
        this.form.tax_bank_no = data.tax_bank_no || data.bank_no || "";
        if (data.province) {
          this.form.province = data.province;
          await this.loadCities(data.province);
          this.form.city = data.city || "";
        }
      } catch (error) {
        // 企业资料不存在时仍允许手工录入。
      }
    },
    syncTaxName(value) {
      if (this.mode === "add") this.form.tax_name = value;
    },
    handleProvinceChange(value) {
      this.form.city = "";
      this.loadCities(value);
    },
    handleEffectDateChange(value) {
      if (!value || this.mode !== "add") return;
      this.form.expire_date = dayjs(value)
        .add(1, "year")
        .subtract(1, "second")
        .format("YYYY-MM-DD HH:mm:ss");
    },
    calculateRate() {
      const price = Number(this.form.price);
      const income = Number(this.form.estimated_operating_income);
      this.form.rate =
        price > 0 && income > 0
          ? ((price * 100) / (income * 10000)).toFixed(4)
          : "";
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        if (
          this.form.tax_name &&
          this.form.tax_name !== this.form.policyholder
        ) {
          this.$message.warning("开票企业名称必须与投保人一致");
          return;
        }
        this.submitting = true;
        try {
          const data = this.buildPayload();
          const res = await saveOfflineBusinessOrder(data);
          if (!res) return;
          this.$message.success(this.mode === "add" ? "新增成功" : "保存成功");
          this.dialogVisible = false;
          this.$emit("success");
        } finally {
          this.submitting = false;
        }
      });
    },
    buildPayload() {
      const data = {
        channel_id: Number(this.form.channel_id),
        insurance_type: Number(this.form.insurance_type),
        policyholder: this.form.policyholder,
        beneficiary: this.form.beneficiary,
        operator_mobile: this.form.operator_mobile,
        province: this.form.province,
        city: this.form.city,
        vat_invoice_open: Number(this.form.vat_invoice_open),
        estimated_operating_income: this.toInteger(
          this.form.estimated_operating_income,
          10000
        ),
        business_license_url: this.form.business_license_url,
        policy_no: this.form.policy_no,
        single_coverage: this.toInteger(this.form.single_coverage, 10000),
        total_coverage: this.toInteger(this.form.total_coverage, 10000),
        effect_date: this.form.effect_date,
        expire_date: this.form.expire_date,
        price: this.toInteger(this.form.price, 100),
        rate: this.toInteger(this.form.rate, 10000),
        policy_image_urls: this.form.policy_image_urls,
        form_image_urls: this.form.form_image_urls,
        deductible: this.form.deductible,
        clause: this.form.clause,
        terms: this.form.terms
      };
      if (this.form.id) data.id = Number(this.form.id);
      if (this.form.vat_invoice_open === 1) {
        data.vat_invoice_type = Number(this.form.vat_invoice_type);
        if (this.form.vat_invoice_type === 1) {
          data.vat_invoice_title = Number(this.form.vat_invoice_title);
          if (this.form.vat_invoice_title === 1) {
            data.tax_name = this.form.tax_name;
            data.tax_no = this.form.tax_no;
          } else {
            data.individual_title_name = this.form.individual_title_name;
          }
        } else {
          Object.assign(data, {
            tax_name: this.form.tax_name,
            tax_no: this.form.tax_no,
            tax_address: this.form.tax_address,
            tax_mobile: this.form.tax_mobile,
            tax_bank: this.form.tax_bank,
            tax_bank_no: this.form.tax_bank_no,
            tax_file_url: this.form.tax_file_url,
            vat_file_url: this.form.vat_file_url
          });
        }
      }
      return data;
    },
    resetForm() {
      this.form = createForm();
      this.cityList = [];
      if (this.$refs.form) this.$refs.form.clearValidate();
    },
    parseUrls(value) {
      if (!value) return [];
      if (Array.isArray(value))
        return value
          .map(item => (typeof item === "string" ? item : item && item.url))
          .filter(Boolean);
      if (typeof value === "object") return value.url ? [value.url] : [];
      const text = String(value).trim();
      try {
        return this.parseUrls(JSON.parse(text));
      } catch (error) {
        return text.indexOf(",") > -1
          ? text
              .split(",")
              .map(item => item.trim())
              .filter(Boolean)
          : [text];
      }
    },
    formatDate(value) {
      if (!value) return "";
      const timestamp = /^\d{10}$/.test(String(value))
        ? Number(value) * 1000
        : value;
      const date = dayjs(timestamp);
      return date.isValid() ? date.format("YYYY-MM-DD HH:mm:ss") : "";
    },
    toUnit(value, divisor) {
      if (value === "" || value == null) return undefined;
      return Number(value) / divisor;
    },
    toInteger(value, multiplier) {
      if (value === "" || value == null) return 0;
      const number = Number(value);
      return Number.isFinite(number) ? Math.round(number * multiplier) : 0;
    },
    channelLabel(item) {
      const name = item.name || item.realname || item.channel_name || "";
      return item.alias ? `${name}（${item.alias}）` : name;
    },
    provinceName(item) {
      return typeof item === "string"
        ? item
        : item.province || item.text || item.name || "";
    },
    cityName(item) {
      return typeof item === "string"
        ? item
        : item.city || item.text || item.name || "";
    }
  }
};
</script>

<style lang="less" scoped>
.section-title {
  margin: 8px 0 18px;
  padding-left: 10px;
  border-left: 3px solid #409eff;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}
</style>
